/**
 * Hand-written SVG charts (offline, no chart library). Mark specs follow the dataviz method:
 * bars ≤ 24px with a 4px rounded data-end and a square baseline, 2px surface gap between
 * adjacent bars, 2px lines, ≥ 8px end markers with a surface ring, hairline recessive grid,
 * one y-axis, legend for ≥ 2 series, values in ink (never in the series colour), hover tooltip.
 * Series colours: income #0a8aa3 / expenses #d46a1f (palette validated for CVD and contrast).
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { formatAxisNumber, formatEurPlain } from "@/lib/format";

export const SERIES_COLORS = { income: "#0a8aa3", expense: "#d46a1f", balance: "#0f5563" } as const;
const GRID = "#e3e8e6";
const AXIS_TEXT = "#5d6d70";

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Clean tick values covering [min, max], always including 0. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const lo = Math.min(0, min);
  const hi = Math.max(0, max);
  if (lo === hi) return [0, 1];
  const rough = (hi - lo) / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough;
  const start = Math.floor(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= hi + step * 0.5; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

/** Path for a bar with a 4px rounded data-end and a square baseline end. */
function barPath(x: number, w: number, yBase: number, yEnd: number): string {
  const up = yEnd <= yBase;
  const h = Math.abs(yBase - yEnd);
  const r = Math.min(4, w / 2, h);
  if (h === 0) return "";
  if (up) {
    return `M${x},${yBase} V${yEnd + r} Q${x},${yEnd} ${x + r},${yEnd} H${x + w - r} Q${x + w},${yEnd} ${x + w},${yEnd + r} V${yBase} Z`;
  }
  return `M${x},${yBase} V${yEnd - r} Q${x},${yEnd} ${x + r},${yEnd} H${x + w - r} Q${x + w},${yEnd} ${x + w},${yEnd - r} V${yBase} Z`;
}

export interface Series {
  key: string;
  label: string;
  color: string;
  values: number[];
}

function Legend({ series }: { series: Series[] }) {
  if (series.length < 2) return null;
  return (
    <ul className="mb-2 flex flex-wrap gap-4 text-sm text-muted">
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-2">
          <span className="inline-block size-3 rounded-sm" style={{ background: s.color }} aria-hidden="true" />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

function Tooltip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const left = Math.min(Math.max(x - 80, 0), Math.max(width - 160, 0));
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-0 z-10 w-40 rounded-lg border border-line bg-surface px-3 py-2 text-sm shadow-sm"
      style={{ left }}
    >
      {children}
    </div>
  );
}

const PAD = { top: 12, right: 8, bottom: 26, left: 48 };

/**
 * Grouped columns over categories (e.g. months). Supports negative values: bars grow from the
 * zero baseline, up or down. `colorFor` can colour a single series by sign (money in / out).
 */
export function ColumnChart({
  labels,
  series,
  height = 220,
  ariaLabel,
  tooltipTitles,
  colorFor,
}: {
  labels: string[];
  series: Series[];
  height?: number;
  ariaLabel: string;
  tooltipTitles?: string[];
  colorFor?: (seriesKey: string, value: number) => string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const all = series.flatMap((s) => s.values);
  const ticks = niceTicks(Math.min(...all, 0), Math.max(...all, 0));
  const lo = ticks[0]!;
  const hi = ticks[ticks.length - 1]!;
  const plotW = Math.max(width - PAD.left - PAD.right, 0);
  const plotH = height - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + ((hi - v) / (hi - lo || 1)) * plotH;
  const band = labels.length ? plotW / labels.length : 0;
  const gap = 2;
  const barW = Math.min(24, Math.max((band * 0.7 - gap * (series.length - 1)) / series.length, 2));
  const groupW = barW * series.length + gap * (series.length - 1);

  return (
    <figure className="m-0">
      <Legend series={series} />
      <div ref={ref} className="relative w-full" onPointerLeave={() => setHover(null)}>
        {width > 0 ? (
          <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? "#b9c4c1" : GRID} strokeWidth={1} />
                <text x={PAD.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill={AXIS_TEXT}>
                  {formatAxisNumber(t)}
                </text>
              </g>
            ))}
            {labels.map((label, i) => {
              const x0 = PAD.left + band * i + (band - groupW) / 2;
              return (
                <g key={label}>
                  {hover === i ? <rect x={PAD.left + band * i} y={PAD.top} width={band} height={plotH} fill="#0f556310" /> : null}
                  {series.map((s, j) => {
                    const v = s.values[i] ?? 0;
                    return <path key={s.key} d={barPath(x0 + j * (barW + gap), barW, y(0), y(v))} fill={colorFor ? colorFor(s.key, v) : s.color} />;
                  })}
                  <text x={PAD.left + band * i + band / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>
                    {label}
                  </text>
                  <rect
                    x={PAD.left + band * i}
                    y={PAD.top}
                    width={band}
                    height={plotH + PAD.bottom}
                    fill="transparent"
                    onPointerEnter={() => setHover(i)}
                    onPointerDown={() => setHover(i)}
                  />
                </g>
              );
            })}
          </svg>
        ) : (
          <div style={{ height }} />
        )}
        {hover !== null ? (
          <Tooltip x={PAD.left + band * hover + band / 2} width={width}>
            <p className="font-medium">{tooltipTitles?.[hover] ?? labels[hover]}</p>
            {series.map((s) => (
              <p key={s.key} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-muted">
                  <span className="inline-block size-2 rounded-full" style={{ background: s.color }} aria-hidden="true" />
                  {s.label}
                </span>
                <span className="amount">{formatEurPlain(String(s.values[hover] ?? 0))}</span>
              </p>
            ))}
          </Tooltip>
        ) : null}
      </div>
    </figure>
  );
}

/** Single-series line with a 10% area wash (e.g. closing balance per month). Handles negatives. */
export function LineChart({
  labels,
  values,
  color = SERIES_COLORS.balance,
  height = 220,
  ariaLabel,
  tooltipTitles,
  valueLabel,
}: {
  labels: string[];
  values: number[];
  color?: string;
  height?: number;
  ariaLabel: string;
  tooltipTitles?: string[];
  valueLabel: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const ticks = niceTicks(Math.min(...values, 0), Math.max(...values, 0));
  const lo = ticks[0]!;
  const hi = ticks[ticks.length - 1]!;
  const plotW = Math.max(width - PAD.left - PAD.right - 8, 0);
  const plotH = height - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (labels.length > 1 ? (plotW * i) / (labels.length - 1) : plotW / 2);
  const y = (v: number) => PAD.top + ((hi - v) / (hi - lo || 1)) * plotH;
  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const last = values.length - 1;

  return (
    <figure className="m-0">
      <div ref={ref} className="relative w-full" onPointerLeave={() => setHover(null)}>
        {width > 0 && values.length > 0 ? (
          <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? "#b9c4c1" : GRID} strokeWidth={1} />
                <text x={PAD.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill={AXIS_TEXT}>
                  {formatAxisNumber(t)}
                </text>
              </g>
            ))}
            <polygon points={`${x(0)},${y(0)} ${points} ${x(last)},${y(0)}`} fill={color} fillOpacity={0.1} />
            <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {labels.map((label, i) =>
              labels.length <= 12 || i % Math.ceil(labels.length / 12) === 0 ? (
                <text key={label} x={x(i)} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>
                  {label}
                </text>
              ) : null,
            )}
            {hover !== null ? <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke="#9aa7a4" strokeWidth={1} /> : null}
            <circle cx={x(hover ?? last)} cy={y(values[hover ?? last]!)} r={5} fill={color} stroke="#ffffff" strokeWidth={2} />
            {labels.map((label, i) => (
              <rect
                key={`hit-${label}`}
                x={x(i) - (plotW / Math.max(labels.length - 1, 1)) / 2}
                y={PAD.top}
                width={plotW / Math.max(labels.length - 1, 1)}
                height={plotH + PAD.bottom}
                fill="transparent"
                onPointerEnter={() => setHover(i)}
                onPointerDown={() => setHover(i)}
              />
            ))}
          </svg>
        ) : (
          <div style={{ height }} />
        )}
        {hover !== null ? (
          <Tooltip x={x(hover)} width={width}>
            <p className="font-medium">{tooltipTitles?.[hover] ?? labels[hover]}</p>
            <p className="flex justify-between gap-2">
              <span className="text-muted">{valueLabel}</span>
              <span className="amount">{formatEurPlain(String(values[hover]))}</span>
            </p>
          </Tooltip>
        ) : null}
      </div>
    </figure>
  );
}

/**
 * Budget meter: the fill carries the state (petrol within budget, over-budget red with a text
 * label — never colour alone); the track is a lighter step of the same hue.
 */
export function BudgetMeter({ actual, budget }: { actual: number; budget: number }) {
  if (budget <= 0) return null;
  const ratio = actual / budget;
  const over = ratio > 1;
  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      <div className={`h-2 flex-1 overflow-hidden rounded-full ${over ? "bg-over-soft" : "bg-petrol-soft"}`}>
        <div className={`h-full rounded-full ${over ? "bg-over" : "bg-petrol"}`} style={{ width: `${Math.min(ratio, 1) * 100}%` }} />
      </div>
    </div>
  );
}
